import React, { useEffect, useState } from "react";
import {
  PageHeader,
  Form,
  Input,
  InputNumber,
  Button,
  Card,
  Row,
  Col,
  Switch,
  Select,
  Divider,
  Spin,
} from "antd";
import { SettingOutlined } from "@ant-design/icons";
import { handleApiSuccess, handleApiError, MyBreadcrumb } from "utils";
import {
  billingSettingsGetService,
  billingSettingsUpdateService,
} from "services/billing";

const { Option } = Select;

export default function BillingSettings() {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    billingSettingsGetService()
      .then((response) => {
        setIsLoading(false);
        if (response.data.item) form.setFieldsValue(response.data.item);
      })
      .catch((error) => {
        setIsLoading(false);
        handleApiError(error);
      });
  }, [form]);

  const onFinish = (values) => {
    setSaving(true);
    billingSettingsUpdateService(values)
      .then((response) => {
        setSaving(false);
        handleApiSuccess(response);
        if (response.data.item) form.setFieldsValue(response.data.item);
      })
      .catch((error) => {
        setSaving(false);
        handleApiError(error);
      });
  };

  return (
    <div>
      <MyBreadcrumb
        items={[
          { label: "Home", path: "/" },
          { label: "Billing", path: "" },
          { label: "Settings", path: "" },
        ]}
      />
      <PageHeader
        ghost={false}
        avatar={{ icon: <SettingOutlined /> }}
        title="Billing Settings"
        subTitle="Invoice numbering, terms and payment gateways"
      />
      <Spin spinning={isLoading}>
        <Form form={form} layout="vertical" onFinish={onFinish} style={{ marginTop: 16 }}>
          <Card title="Invoice Numbering" size="small" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item label="Prefix" name="invoice_prefix">
                  <Input placeholder="INV-" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  label="Next Number"
                  name="invoice_next_number"
                  help="The next invoice will use this number, then increment."
                >
                  <InputNumber min={0} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label="Number Padding (digits)" name="invoice_number_padding">
                  <InputNumber min={0} max={12} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Card title="Invoice Defaults" size="small" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item label="Default Due (days)" name="default_due_days">
                  <InputNumber min={0} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label="Default Currency" name="default_currency">
                  <Input placeholder="USD" />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item
              label="Default Terms & Conditions"
              name="default_terms_conditions"
              help="Pre-filled on every new invoice. Editable per invoice."
            >
              <Input.TextArea rows={4} />
            </Form.Item>
            <Form.Item label="Default Client Note" name="default_client_note">
              <Input.TextArea rows={2} />
            </Form.Item>
          </Card>

          <Card title="Branding" size="small" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item label="Company Display Name" name="company_display_name">
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Logo URL" name="company_logo_url">
                  <Input placeholder="https://..." />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label="Email" name="company_email">
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label="Phone" name="company_phone">
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label="Address" name="company_address">
                  <Input />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Card title="Payment Gateways" size="small" style={{ marginBottom: 16 }}>
            <Row gutter={16} align="middle">
              <Col xs={12} md={6}>
                <Form.Item label="Stripe Enabled" name="stripe_enabled" valuePropName="checked">
                  <Switch />
                </Form.Item>
              </Col>
              <Col xs={24} md={18}>
                <Form.Item label="Stripe Publishable Key" name="stripe_publishable_key">
                  <Input placeholder="pk_live_..." />
                </Form.Item>
              </Col>
            </Row>
            <Divider style={{ margin: "8px 0" }} />
            <Row gutter={16} align="middle">
              <Col xs={12} md={6}>
                <Form.Item label="PayPal Enabled" name="paypal_enabled" valuePropName="checked">
                  <Switch />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="PayPal Client ID" name="paypal_client_id">
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item label="Mode" name="paypal_mode">
                  <Select>
                    <Option value="sandbox">Sandbox</Option>
                    <Option value="live">Live</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Divider style={{ margin: "8px 0" }} />
            <Row gutter={16} align="middle">
              <Col xs={12} md={6}>
                <Form.Item label="Crypto Enabled" name="crypto_enabled" valuePropName="checked">
                  <Switch />
                </Form.Item>
              </Col>
              <Col xs={24} md={9}>
                <Form.Item label="BTC Address" name="crypto_btc_address">
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} md={9}>
                <Form.Item label="ETH Address" name="crypto_eth_address">
                  <Input />
                </Form.Item>
              </Col>
            </Row>
            <p style={{ color: "#888", fontSize: 12 }}>
              Secret API keys are stored securely on the server (environment), not here.
            </p>
          </Card>

          <Button type="primary" htmlType="submit" loading={saving}>
            Save Settings
          </Button>
        </Form>
      </Spin>
    </div>
  );
}
